import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Alert,
  Collapse,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { keyframes } from "@mui/system";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import AuthService from "../services/auth.service";
import { addProfileInformation } from "../features/cart/cartSlice";
import { tokens } from "../theme";

// Brand palette lifted from the Storemax logo (navy → steel blue, silver "MAX").
// Kept local to this screen so the global theme is untouched.
const BRAND = {
  navyDeep: "#0A2038",
  navy: "#12395F",
  blue: "#1E5B8F",
  blueBright: "#2E7DB8",
  steel: "#5B9BD5",
  silver: "#9AA7B4",
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Login = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [touched, setTouched] = useState({ username: false, password: false });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const usernameError = touched.username && !username.trim();
  const passwordError = touched.password && !password;

  const handleLogin = (e) => {
    e.preventDefault();
    setMessage("");
    setTouched({ username: true, password: true });

    if (!username.trim() || !password) return;

    setLoading(true);
    AuthService.login(username, password)
      .then((user) => {
        navigate("/");
        localStorage.setItem("user", true);
        dispatch(addProfileInformation({ id: user.id, loggedIn: true }));
      })
      .catch((error) => {
        const resMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        setLoading(false);
        setMessage(resMessage);
      });
  };

  useEffect(() => {
    AuthService.getCurrentUser().then((user) => {
      if (user) navigate("/");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputBg = isDark ? "#16233B" : "#ffffff";
  const inputText = colors.grey[100];

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: inputBg,
      transition: "border-color .15s ease, box-shadow .15s ease",
      "& fieldset": {
        borderColor: isDark ? colors.grey[700] : colors.grey[300],
      },
      "&:hover fieldset": {
        borderColor: BRAND.steel,
      },
      "&.Mui-focused fieldset": {
        borderColor: BRAND.blueBright,
        borderWidth: "2px",
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 4px ${BRAND.blueBright}22`,
      },
    },
    // Pin the typed text light-on-dark in every state — otherwise MUI's default
    // color takes over once Chrome drops the autofill state and text goes black.
    "& .MuiOutlinedInput-input": {
      color: inputText,
      WebkitTextFillColor: inputText,
      "&::placeholder": { color: colors.grey[500], opacity: 1 },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: BRAND.blueBright,
    },
    // Chrome/Safari autofill is a UA-level style — it only yields to !important.
    // Kills the blue box AND stops the text going dark on focus.
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active":
      {
        WebkitBoxShadow: `0 0 0 100px ${inputBg} inset !important`,
        WebkitTextFillColor: `${inputText} !important`,
        caretColor: inputText,
        borderRadius: "inherit",
        transition: "background-color 9999s ease-in-out 0s",
      },
  };

  return (
    <Box
      display="flex"
      minHeight="100vh"
      width="100%"
      sx={{ backgroundColor: isDark ? colors.primary[500] : "#f4f6f9" }}
    >
      {/* Left brand panel */}
      {!isSmall && (
        <Box
          flex="1"
          position="relative"
          overflow="hidden"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          p={7}
          sx={{
            background: `linear-gradient(150deg, ${BRAND.navyDeep} 0%, ${BRAND.navy} 55%, ${BRAND.blue} 115%)`,
            color: "#fff",
          }}
        >
          {/* Warehouse-racking grid — a subtle nod to the business */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `linear-gradient(${BRAND.steel}22 1px, transparent 1px),
                linear-gradient(90deg, ${BRAND.steel}22 1px, transparent 1px)`,
              backgroundSize: "46px 46px",
              maskImage:
                "radial-gradient(circle at 30% 40%, black, transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(circle at 30% 40%, black, transparent 75%)",
            }}
          />
          {/* Depth glow */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: "-140px",
              right: "-120px",
              width: "420px",
              height: "420px",
              borderRadius: "50%",
              background: `radial-gradient(circle at 30% 30%, ${BRAND.steel}55, transparent 70%)`,
              filter: "blur(14px)",
            }}
          />

          {/* Wordmark */}
          <Typography
            variant="h4"
            fontWeight={800}
            letterSpacing="4px"
            zIndex={1}
          >
            <span style={{ color: "#fff" }}>STORE</span>
            <span style={{ color: BRAND.silver }}>MAX</span>
          </Typography>

          <Box zIndex={1} maxWidth="440px">
            <Typography variant="h2" fontWeight={800} lineHeight={1.15} mb={2}>
              Welcome back.
            </Typography>
            <Typography variant="h6" fontWeight={400} sx={{ opacity: 0.82 }}>
              Sign in to manage your quotations, carts, and inventory everything
              is right where you left it.
            </Typography>
          </Box>

          <Box zIndex={1}>
            <Typography
              variant="body2"
              sx={{
                letterSpacing: "3px",
                textTransform: "uppercase",
                color: BRAND.steel,
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              Material Handling Experts
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.5 }}>
              © {new Date().getFullYear()} Storemax. All rights reserved.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Right form panel */}
      <Box
        flex="1"
        position="relative"
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={3}
      >
        {/* Soft brand halos so the dark side isn't a flat void */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "-10%",
            right: "-6%",
            width: 460,
            height: 460,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.blue}${isDark ? "40" : "18"}, transparent 70%)`,
            filter: "blur(50px)",
            pointerEvents: "none",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: "-14%",
            left: "-8%",
            width: 420,
            height: 420,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.steel}${isDark ? "33" : "14"}, transparent 70%)`,
            filter: "blur(55px)",
            pointerEvents: "none",
          }}
        />

        <Box
          component="form"
          onSubmit={handleLogin}
          noValidate
          width="100%"
          maxWidth="410px"
          sx={{
            position: "relative",
            zIndex: 1,
            animation: `${fadeUp} .5s cubic-bezier(.2,.7,.2,1) both`,
            backgroundColor: isDark ? colors.primary[400] : "#fff",
            // subtle brand-tinted sheen from the top-left for depth
            backgroundImage: isDark
              ? `linear-gradient(160deg, ${BRAND.steel}1a, transparent 42%)`
              : `linear-gradient(160deg, ${BRAND.blue}0d, transparent 42%)`,
            borderRadius: "22px",
            p: { xs: 3, sm: 5 },
            border: `1px solid ${isDark ? colors.grey[800] : colors.grey[200]}`,
            boxShadow: isDark
              ? "0 24px 70px rgba(0,0,0,0.5)"
              : "0 24px 70px rgba(18,57,95,0.12)",
            // thin brand accent riding the top edge
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "10%",
              right: "10%",
              height: "3px",
              borderRadius: "0 0 4px 4px",
              background: `linear-gradient(90deg, transparent, ${BRAND.blueBright}, ${BRAND.steel}, transparent)`,
            },
          }}
        >
          {/* Custom Storemax brand mark — an isometric storage cube drawn in
              SVG (steel-blue facets, echoing the real logo) + theme-aware
              wordmark. Fully vector: no raster, no white background box. */}
          <Box display="flex" alignItems="center" gap={1.25} mb={3.5}>
            <Box
              component="svg"
              viewBox="0 0 48 48"
              sx={{
                width: 44,
                height: 44,
                display: "block",
                filter: `drop-shadow(0 6px 14px ${BRAND.navy}77)`,
              }}
            >
              <defs>
                <linearGradient id="smxTop" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#AECEE8" />
                  <stop offset="100%" stopColor="#7FB0D8" />
                </linearGradient>
                <linearGradient id="smxLeft" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3E80B5" />
                  <stop offset="100%" stopColor="#2A6096" />
                </linearGradient>
                <linearGradient id="smxRight" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#21507D" />
                  <stop offset="100%" stopColor="#143A5C" />
                </linearGradient>
              </defs>
              {/* top face */}
              <polygon points="24,11.5 37,18 24,24.5 11,18" fill="url(#smxTop)" />
              {/* left face */}
              <polygon points="11,18 24,24.5 24,39.5 11,33" fill="url(#smxLeft)" />
              {/* right face */}
              <polygon points="37,18 24,24.5 24,39.5 37,33" fill="url(#smxRight)" />
            </Box>
            <Typography
              variant="h4"
              fontWeight={800}
              letterSpacing="2px"
              lineHeight={1}
            >
              <span style={{ color: isDark ? "#fff" : BRAND.navy }}>STORE</span>
              <span style={{ color: BRAND.steel }}>MAX</span>
            </Typography>
          </Box>

          <Typography
            variant="h3"
            fontWeight={800}
            mb={0.5}
            color={colors.grey[100]}
          >
            Sign in
          </Typography>
          <Typography variant="body2" color={colors.grey[400]} mb={3.5}>
            Enter your details to access your account.
          </Typography>

          <Collapse in={!!message}>
            <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>
              {message}
            </Alert>
          </Collapse>

          <TextField
            fullWidth
            label="Username"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, username: true }))}
            error={usernameError}
            helperText={usernameError ? "Username is required" : " "}
            autoComplete="username"
            autoFocus
            sx={{ ...fieldSx, mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineRoundedIcon sx={{ color: colors.grey[400] }} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            error={passwordError}
            helperText={passwordError ? "Password is required" : " "}
            autoComplete="current-password"
            sx={{ ...fieldSx, mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: colors.grey[400] }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Remember me */}
          <Box display="flex" alignItems="center" mb={3} mt={0.5}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  size="small"
                  sx={{
                    color: colors.grey[500],
                    p: 0.5,
                    mr: 0.5,
                    "&.Mui-checked": { color: BRAND.blueBright },
                  }}
                />
              }
              label={
                <Typography variant="body2" color={colors.grey[300]}>
                  Remember me
                </Typography>
              }
            />
          </Box>

          <Button
            type="submit"
            fullWidth
            disabled={loading}
            endIcon={!loading && <ArrowForwardRoundedIcon />}
            sx={{
              background: `linear-gradient(135deg, ${BRAND.blue} 0%, ${BRAND.navy} 100%)`,
              color: "#fff",
              fontSize: "15px",
              fontWeight: 700,
              letterSpacing: "0.3px",
              textTransform: "none",
              borderRadius: "12px",
              py: 1.5,
              boxShadow: `0 10px 24px ${BRAND.navy}55`,
              transition: "transform .15s ease, box-shadow .15s ease",
              "& .MuiButton-endIcon": { transition: "transform .18s ease" },
              "&:hover": {
                background: `linear-gradient(135deg, ${BRAND.blueBright} 0%, ${BRAND.blue} 100%)`,
                boxShadow: `0 12px 30px ${BRAND.navy}66`,
                transform: "translateY(-1px)",
              },
              "&:hover .MuiButton-endIcon": { transform: "translateX(3px)" },
              "&.Mui-disabled": {
                background: colors.grey[700],
                color: colors.grey[400],
                boxShadow: "none",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Sign in"
            )}
          </Button>

          <Typography
            variant="body2"
            color={colors.grey[400]}
            textAlign="center"
            mt={4}
          >
            Don't have an account?{" "}
            <Box
              component={Link}
              to="/register"
              sx={{
                color: isDark ? BRAND.steel : BRAND.blue,
                fontWeight: 700,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Create one
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
